import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import { injectIntl } from 'react-intl';
import styled, { withTheme } from 'styled-components/macro';
import validator from 'validator';
import BuyButton from '@tovia/man-ui/lib/components/Button/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/pro-light-svg-icons';
import { ApiClient } from 'src/client/helpers';
import urls, { constructUrl } from 'src/shared/urls';
import { validatorOptions } from 'src/shared/constants/signup';

export class PurchaseGalleryFormClass extends Component {
  static propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    className: PropTypes.string,
    theme: PropTypes.object.isRequired, // eslint-disable-line react/no-unused-prop-types
    intl: PropTypes.object.isRequired,
    asyncValidating: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    invalid: PropTypes.bool,
    submitted: PropTypes.bool,
  };

  static defaultProps = {
    className: '',
    asyncValidating: false,
    invalid: false,
    submitted: false,
  };

  renderInputField = ({
    meta: { error, touched, asyncValidating },
    input,
    className,
    inputClass,
    errorClass,
    ...rest
  }) => (
    <div className={className}>
      {asyncValidating && <FontAwesomeIcon icon={faCircleNotch} className="icon right-icon fa-spin" />}
      <input
        {...input}
        {...rest}
        className={`${inputClass}${asyncValidating ? ' validating' : ''}`}
        disabled={rest.disabled}
      />
      {touched && error && <span className={errorClass}>{error}</span>}
    </div>
  );

  render() {
    const { handleSubmit, className, intl, asyncValidating, invalid, submitted } = this.props;

    return (
      <form onSubmit={handleSubmit} className={className}>
        <Field
          inputClass="input"
          errorClass="error"
          component={this.renderInputField}
          id="email-gallery-purchase"
          name="email"
          placeholder="Enter your email"
          type="email"
        />
        <Field
          inputClass="input"
          errorClass="error"
          component={this.renderInputField}
          id="password-gallery-purchase"
          name="password"
          placeholder="Enter your password"
          type="password"
        />
        <BuyButton
          className="submit-button btn btn-primary"
          loading={submitted && !invalid}
          type="submit"
          disabled={submitted || !!asyncValidating || invalid}
          spinnerClass="buy-spinner"
        >
          {intl.formatMessage({ id: 'purchaseGallery.form.button', defaultMessage: 'Continue to watch' })}
        </BuyButton>
      </form>
    );
  }
}

const validate = ({ email, password }) => {
  const errors = {};

  if (!email || !validator.isEmail(email, validatorOptions)) {
    errors.email = 'Invalid email address';
  }

  if (!password || password.length < 4) {
    errors.password = 'Invalid password';
  }

  return errors;
};

const asyncValidate = async ({ email }, dispatch, props, blurredField) => {
  if (!blurredField) {
    return;
  }

  const errors = {};
  const api = new ApiClient();
  try {
    const {
      body: { account },
    } = await api.get(constructUrl(urls.get.accountAvailable), {
      params: {
        email,
      },
    });

    if (account) {
      errors.email = 'Please login to continue with this email';
    }
  } catch (error) {
    if (error.response && error.response.status === 429) {
      errors.email = 'Too many requests attempted, try again later';
    } else {
      errors.email = 'Unable to verify email address';
    }
  }

  if (errors.email) {
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    throw errors;
  }
};

export default reduxForm({
  form: 'purchaseGalleryForm',
  validate,
  asyncValidate,
  asyncBlurFields: ['email'],
})(
  injectIntl(
    withTheme(styled(PurchaseGalleryFormClass)`
      & div:first-child {
        padding-bottom: 12px;
      }

      & .error {
        display: block;
        margin-top: 4px;
        color: red;
      }

      & .input {
        display: block;
        height: 44px;
        padding-left: 10px;
        width: 100%;
        border: 1px solid #888;
        border-radius: 3px;

        &.validating {
          padding-right: 34px;
        }
      }

      & .submit-button {
        position: relative;
        border: 0 none;
        display: inline-block;
        flex-grow: 1;
        font-size: 1.3rem;
        height: 48px;
        margin: 16px 0;
        text-align: center;
        text-transform: uppercase;
        width: 100%;

        &.loading {
          background-color: #9d9d9d;
          color: transparent !important;
        }

        .buy-spinner {
          display: block;
          height: 0;
          position: relative;
          width: 100%;

          .spinner {
            width: 28px;
            height: 28px;

            path {
              fill: #888888;
            }
          }
        }
      }

      & .icon {
        height: 24px;
        margin: 12px 0 0 16px;
        position: absolute;
        width: 24px;
        opacity: 0.5;
      }

      & .right-icon {
        margin: 10px 22px 0 0;
        right: 0;
      }
    `),
  ),
);
